import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../api/vendly';
import ProductCard from '../components/ProductCard';
import styles from './HomePage.module.css';

const banners = [
  { label: 'Electronics', desc: 'Latest tech deals', color: '#1A1814', accent: '#E8420A', img: 'https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=600&q=80' },
  { label: 'Fashion', desc: 'New arrivals weekly', color: '#1C1040', accent: '#7C3AED', img: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80' },
  { label: 'Home & Living', desc: 'Elevate your space', color: '#0F1F1A', accent: '#059669', img: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80' },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      productsAPI.list({ per_page: 4 }),
      productsAPI.list({ per_page: 4, sort: 'rating' }),
    ])
      .then(([featRes, topRes]) => {
        setFeatured(featRes.data.products);
        setTopRated(topRes.data.products);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const Skeletons = () => (
    <div className={styles.grid}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={styles.skeleton} />
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={`container ${styles.heroInner}`}>
          <div className={styles.heroText}>
            <div className={styles.heroEyebrow}>🇳🇬 Nigeria's marketplace</div>
            <h1 className={styles.heroTitle}>
              Shop everything,<br />
              <span className={styles.heroAccent}>delivered fast</span>
            </h1>
            <p className={styles.heroSub}>
              Thousands of products across electronics, fashion, home, beauty and more. Trusted sellers. Secure checkout.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/shop" className={styles.ctaPrimary}>Shop now</Link>
              <Link to="/shop?cat=deals" className={styles.ctaSecondary}>View deals →</Link>
            </div>
          </div>
          <div className={styles.heroStats}>
            {[['50K+', 'Products'], ['10K+', 'Sellers'], ['2M+', 'Customers'], ['4.8★', 'Rating']].map(([n, l]) => (
              <div key={l} className={styles.stat}>
                <span className={styles.statNum}>{n}</span>
                <span className={styles.statLabel}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category banners */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.banners}>
            {banners.map(b => (
              <Link key={b.label} to={`/shop?cat=${b.label.toLowerCase()}`} className={styles.banner} style={{ background: b.color }}>
                <img src={b.img} alt={b.label} className={styles.bannerImg} />
                <div className={styles.bannerOverlay} />
                <div className={styles.bannerContent}>
                  <span className={styles.bannerTag} style={{ background: b.accent }}>{b.label}</span>
                  <p className={styles.bannerDesc}>{b.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHead}>
            <div>
              <h2 className={styles.sectionTitle}>Featured products</h2>
              <p className={styles.sectionSub}>Handpicked deals just for you</p>
            </div>
            <Link to="/shop" className={styles.seeAll}>See all →</Link>
          </div>
          {loading ? <Skeletons /> : (
            <div className={styles.grid}>
              {featured.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Promo banner */}
      <section className={styles.promoSection}>
        <div className="container">
          <div className={styles.promo}>
            <div className={styles.promoText}>
              <h2 className={styles.promoTitle}>Free delivery on orders over ₦20,000</h2>
              <p className={styles.promoSub}>Valid for Lagos, Abuja & Port Harcourt.</p>
              <Link to="/shop" className={styles.promoBtn}>Shop now</Link>
            </div>
            <div className={styles.promoArt}>🚀</div>
          </div>
        </div>
      </section>

      {/* Top rated */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHead}>
            <div>
              <h2 className={styles.sectionTitle}>Top rated</h2>
              <p className={styles.sectionSub}>Loved by our customers</p>
            </div>
            <Link to="/shop" className={styles.seeAll}>See all →</Link>
          </div>
          {loading ? <Skeletons /> : (
            <div className={styles.grid}>
              {topRated.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={`container ${styles.footerInner}`}>
          <div className={styles.footerBrand}>
            <span className={styles.footerLogo}>vendly</span>
            <p>Nigeria's trusted marketplace for quality products at fair prices.</p>
          </div>
          <div className={styles.footerLinks}>
            {['Shop', 'About', 'Sell on Vendly', 'Help Centre', 'Privacy Policy'].map(l => (
              <a key={l} href="#" className={styles.footerLink}>{l}</a>
            ))}
          </div>
        </div>
        <div className={`container ${styles.footerBottom}`}>
          <span>© 2025 Vendly. All rights reserved.</span>
          <span>Built with ♥ in Nigeria 🇳🇬</span>
        </div>
      </footer>
    </div>
  );
}
